//no itx for this file
import Logger from "../Logger";
import BaseObject from "./BaseObject";
import type Device from "./Device";
import KnxParameterCalculation, { KnxParameterCalculationReference } from "./KnxParameterCalculation";
import BaseModel from "./BaseModel";
import { EP } from "../performance/PerfomanceItem";
import { isDevelopmentEnvironment } from "../estada-config-debug";
import TableInfo from "../components/KnxParameterViewer/KnxTableInfo";
import { showToast } from "../components/Toasts";
const logger = new Logger();

export enum ValueLevels {
  default = 0,
  assign = 0.5,
  estadaPreset = 1,
  device = 2,
  calculatedByRule = 3, // results of Rules in Customizing or by  "Assign" in XML-Productdatas
  calculatedLeftToRight = 3.1, // XML ParameterCalculation in XML-Productdatas
  calculatedRightToLeft = 3.2,

  deleteAfterTemplateChange = 3.5, // special level to mark valueLevel below that should be deleted after template change

  estadaUi = 3.8, // interactive

  interactive = 4,
  debug = 5, // just for development, to set values fixed for testing
  keepLevel = 9, // forced setting for a value, independed of the current level, without changing the levrl. For Undo, Redo
  delete = 99, // just for development, to reset a value in the "..." right of the property
  force = 100, // dieser wert wird zum normalen ValueLevel addiert beim Aufruf von setValue
}

export interface KnxInstanceParam {
  previousValue?: any;
  value?: any;
  valueLevel?: ValueLevels;
  referenced?: boolean;
  uiReferenced?: boolean; // ui referenced, e.g. by a cell in the panel layout
  setByScript?: boolean;
}

export interface KnxInstanceModuleData {
  params: Record<string, KnxInstanceParam>;
  extData?: Record<string, any>;
}

export default class KnxInstanceData extends BaseObject {
  public get className(): string {
    return "KnxInstanceData";
  }

  params: Record<string, KnxInstanceParam> = {};
  extData: any = {};

  modules: Record<string, KnxInstanceModuleData> = {};

  // memory = new Map<string, { value: any, level: number }>()

  reEvalIds = new Set<string>();

  //
  // key = from
  // value = to
  //
  assigns = new Map<string, { toId: string; instanceId: string }>();

  addAssign(fromId: string, toId: string, instanceId: string) {
    this.assigns.set(fromId, {
      toId,
      instanceId,
    });
    //
    // MWE 3.6.2026: (Updateverhalten von GIRA Dali Gateway)
    //
    // wenn eine Zuweisung hinzugefügt wird,
    // muss die SourceId auch in die ReEvalIds aufgenommen werden.
    // Denn es muß ja die Zuweisung neu ausgewertet werden, wenn sich die SourceId ändert.
    //
    // Zur optimierung könnteman überlegen, dass diese nur wahr ist,
    // wenn die TargetId auch eine ReEvalId ist.
    //
    // Im Moment nehme ich erstmal hin, dass wir möglicherweise etwas zu viel rechnen
    //

    // this.addReEvalId(fromId, instanceId);
  }

  getAssignForToId(toId: string, toInstanceId: string): { fromId: string } | undefined {
    for (const [fromId, assign] of this.assigns.entries()) {
      if (assign.toId === toId && assign.instanceId === toInstanceId) {
        return { fromId: fromId };
      }
    }

    return undefined;
  }

  // non persistentParameterCalculations
  parameterCalculations = new Map<string, KnxParameterCalculation>();

  // non persistent mapping from params to calculations
  paramsToCalculationReferences = new Map<string, Map<string, KnxParameterCalculationReference>>();

  //
  // non persistent: TableInfo objects collected during parameter rendering
  // key: instanceId + ":" + id
  //
  tableInfos = new Map<string, TableInfo>();

  constructor(data?: any, parent?: BaseObject) {
    super(data, parent);
  }
  //////////////////////////////

  compareTo(other: any) {
    // TODO: need to do actual comparison of the objects
    return [];
  }

  getPersistentProperties(): string[] {
    return [
      "pesistentReEvalIds",
      "pesistentParams",
      "pesistentModules",
      "stripDone",
      ...super.getPersistentProperties(),
    ];
  }

  //////////////////////////////
  // non persistent
  expanded: any = [];

  //////////////////////////////

  get pesistentReEvalIds(): string | undefined {
    if (this.reEvalIds.size > 0) {
      let res = "";
      this.reEvalIds.forEach((id) => {
        res += id + ";";
      });
      return res;
    } else {
      return undefined;
    }
  }

  set pesistentReEvalIds(newValue: string | undefined) {
    this.clearReEvalIds();
    if (newValue) {
      const ss = newValue.split(";");
      ss.forEach((id) => {
        if (id != "") {
          this.reEvalIds.add(id);
        }
      });
    }
  }

  //
  // filter params to the items with (valueLevel == ValueLevels.device)
  //
  // these properties are ONLY for storeing and loading the data to and from persistent storage
  //

  valueAndLevelToString(p: KnxInstanceParam) {
    let levelPostFix = "";
    if (p.valueLevel != ValueLevels.interactive) {
      levelPostFix = `~${p.valueLevel}`;
    }
    return p.value + levelPostFix;
  }

  stringToValueAndLevel(s: string): any {
    if (s != undefined && typeof s == "string") {
      const ss = s.split("~");
      return {
        value: ss[0],
        valueLevel: ss[1] != undefined ? parseFloat(ss[1]) : ValueLevels.interactive,
      };
    } else {
      return {
        value: s,
        valueLevel: ValueLevels.interactive,
      };
    }
  }

  debugParams = false;

  get pesistentParams(): any {
    return this.getParams(ValueLevels.device);
  }

  getParams(minLevel: ValueLevels): any {
    const res: any = {};
    for (let name in this.params) {
      const p = this.params[name];
      if (p && ((p.valueLevel || 0) >= minLevel || this.debugParams)) {
        res[name] = this.valueAndLevelToString(p);
      }
    }
    return res;
  }

  set pesistentParams(newValue: any) {
    this.params = {};
    for (let name in newValue) {
      const p = newValue[name];

      this.params[name] = this.stringToValueAndLevel(p);
    }
  }

  get pesistentModules(): any {
    return this.getModules(ValueLevels.device);
  }

  getModules(minLevel: ValueLevels): any {
    const res: any = {};
    for (let instanceId in this.modules) {
      let modul = this.modules[instanceId];

      const resDataModul: any = {};
      var cnt = 0;
      for (let id in modul.params) {
        const p = modul.params[id];
        const level = p.valueLevel ?? 0;
        if (level >= minLevel || this.debugParams) {
          resDataModul[id] = this.valueAndLevelToString(p);
          cnt++;
        }
      }
      if (cnt > 0) {
        res[instanceId] = resDataModul;
      }
    }

    return res;
  }

  set pesistentModules(data: any) {
    this.modules = {};

    for (let instanceId in data) {
      const dataModule = data[instanceId];

      const module: KnxInstanceModuleData = { params: {} };
      this.modules[instanceId] = module;

      for (let id in dataModule) {
        const p = dataModule[id];
        module.params[id] = this.stringToValueAndLevel(p);
      }
    }
  }

  ////////////////////////////////////////////////////////////////////////////

  clearValueLevel(valLev: ValueLevels) {
    for (let id in this.params) {
      const p = this.params[id];
      if (p.valueLevel == valLev) {
        delete this.params[id];
      }
    }

    for (let idModule in this.modules) {
      const modul = this.modules[idModule];
      for (let id in modul.params) {
        const p = modul.params[id];
        if (p.valueLevel == valLev) {
          delete modul.params[id];
        }
      }
    }
  }

  clearAllValueLevelsUpTo(valueLevelMax: ValueLevels) {
    for (let id in this.params) {
      const p = this.params[id];
      const level = p.valueLevel ?? 0;
      if (level <= valueLevelMax) {
        delete this.params[id];
      }
    }

    for (let idModule in this.modules) {
      const modul = this.modules[idModule];
      for (let id in modul.params) {
        const p = modul.params[id];
        const level = p.valueLevel ?? 0;
        if (level <= valueLevelMax) {
          delete modul.params[id];
        }
      }
    }
  }

  clearReferenced() {
    for (let id in this.params) {
      const p = this.params[id];
      p.referenced = false;
      p.uiReferenced = false;
    }

    for (let idModule in this.modules) {
      const modul = this.modules[idModule];
      for (let id in modul.params) {
        const p = modul.params[id];
        modul.params[id].referenced = false;
        modul.params[id].uiReferenced = false;
      }
    }
  }

  clearAfterTemplateChange() {
    this.clearReferenced();
    this.clearAllValueLevelsUpTo(ValueLevels.deleteAfterTemplateChange);
  }

  deleteUnrefenced() {
    for (let id in this.params) {
      const p = this.params[id];
      if (!p.referenced) {
        delete this.params[id];
      }
    }

    for (let idModule in this.modules) {
      const modul = this.modules[idModule];
      for (let id in modul.params) {
        const p = modul.params[id];
        if (!p.referenced) {
          delete modul.params[id];
        }
      }

      if (Object.keys(modul.params).length == 0) {
        delete this.modules[idModule];
      }
    }
  }

  clearReEvalCounts() {
    for (let id in this.extData) {
      this.setExtendedData(
        {
          reeval: {
            count: 0,
          },
        },
        id,
        "",
      );
    }

    for (let idModule in this.modules) {
      const modul = this.modules[idModule];
      for (let id in modul.extData) {
        this.setExtendedData(
          {
            reeval: {
              count: 0,
            },
          },
          id,
          idModule,
        );
      }
    }
  }

  savePreviousValues() {
    for (let id in this.params) {
      const p = this.params[id];
      p.previousValue = p.value;
    }

    for (let idModule in this.modules) {
      const modul = this.modules[idModule];
      for (let id in modul.params) {
        const p = modul.params[id];
        p.previousValue = p.value;
      }
    }
  }

  clearReEvalIds() {
    this.reEvalIds.clear();
  }

  checkPreviousValuesForReevaluation() {
    for (let id in this.params) {
      const p = this.params[id];
      if (p.previousValue !== undefined && p.previousValue !== p.value) {
        logger.log(`Value changed for param ${id}: ${p.previousValue} -> ${p.value}`);
        this.increaseReEvalCount(id, "");
      }
    }

    for (let idModule in this.modules) {
      const modul = this.modules[idModule];
      for (let id in modul.params) {
        const p = modul.params[id];
        if (p.previousValue !== undefined && p.previousValue !== p.value) {
          logger.log(`Value changed for param ${idModule}/${id}: ${p.previousValue} -> ${p.value}`);
          this.increaseReEvalCount(id, idModule);
        }
      }
    }
  }

  ////////////////////////////////////////////////////////////////////////////
  // TableInfo management (non persistent)
  ////////////////////////////////////////////////////////////////////////////

  private tableInfoKey(id: string, instanceId: string): string {
    return (instanceId || "") + ":" + id;
  }

  setTableInfo(id: string, instanceId: string, tableInfo: TableInfo): void {
    if (!id) {
      logger.error("setTableInfo: empty id");
      return;
    }
    this.tableInfos.set(this.tableInfoKey(id, instanceId), tableInfo);
  }

  getTableInfo(id: string, instanceId: string): TableInfo | undefined {
    return this.tableInfos.get(this.tableInfoKey(id, instanceId));
  }

  clearTableInfos(): void {
    this.tableInfos.clear();
  }

  addReEvalId(id: string | undefined, instanceId: string) {
    if (id) {
      this.reEvalIds.add(instanceId + id);
    }
  }

  removeReEvalId(id: string | undefined, instanceId: string) {
    if (id) {
      this.reEvalIds.delete(instanceId + id);
    }
  }

  isReEvalId(id: string, instanceId: string): boolean {
    return this.reEvalIds.has(instanceId + id);
  }

  stripDone = false;

  stripPrefix(prefix: string) {
    // just for compatibility for some time .... mwe 4.8.2023

    if (this.stripDone) {
      return;
    }

    if (prefix.length > 0) {
      this.stripDone = true;

      const prefixLength = prefix.length + 1;

      for (let id in this.params) {
        if (id.startsWith(prefix)) {
          const shortId = id.substr(prefixLength);
          logger.log(`param: ${id} -> ${shortId} : ${this.params[id]}`);
          this.params[shortId] = this.params[id];
          delete this.params[id];
        } else {
          if (!id.startsWith("P-")) {
            logger.log(`suspect param: ${id}  ${JSON.stringify(this.params[id])}`);
          }
        }
      }

      for (let idModule in this.modules) {
        const modul = this.modules[idModule];

        if (idModule.startsWith(prefix)) {
          const shortIdModul = idModule.substr(prefixLength);
          logger.log(`$modul: {idModule} -> ${shortIdModul}`);
          this.modules[shortIdModul] = this.modules[idModule];
          delete this.modules[idModule];
        }

        for (let id in modul.params) {
          if (id.startsWith(prefix)) {
            const shortId = id.substr(prefixLength);
            logger.log(`${idModule}/${id} -> ${idModule}/${shortId} : ${modul.params[id]}`);
            modul.params[shortId] = modul.params[id];
            delete modul.params[id];
          } else {
            if (id.startsWith("-") || id.startsWith("_")) {
              logger.log(`suspect param: ${id}  ${JSON.stringify(modul.params[id])}`);
            }
          }
        }
      }
    }
  }

  filterRelation(name: string): string {
    return name.split("_R")[0];
  }

  isReferenced(id: string, instanceId: string): boolean {
    if (instanceId) {
      const p = this.modules[instanceId]?.params[id];
      return p?.referenced ?? false;
    } else {
      const p = this.params[id];
      return p?.referenced ?? false;
    }
  }

  getValue(id: string, instanceId?: string, returnUnreferencedParam?: boolean): any {
    if (!id) {
      logger.error("getValue: id empty or undefined");
      return undefined;
    }

    const id1 = this.fromWildCardToFullId(id, instanceId);
    if (!id1) {
      return undefined;
    }

    if (instanceId && !id.startsWith("MD-")) {
      logger.log(`FIXED: get global var ??? id=${id} instanceId=${instanceId}`);
      instanceId = "";
    }

    let res = this.getValueIntern(id1, instanceId, returnUnreferencedParam);
    if (res == undefined) {
      //
      // Es kommt vor,
      //   dass ein Choose innerhalb eines Moduls (z.B.MD-2_M-2, MDT DimAktor AKH-0400.03 )
      //   einen globalen Parameter "P-6_R-5" referenziert
      //
      //   Vermutlich sind alle Parameter innerhalb eines Moduls mit dem Id des Modules geprefixt
      //      in diesem Falle also mit "MD-2_"
      //
      // Mutmassliche Syntax der Paramenter-Namen:
      //
      //     MD-2_UP-71    UnionParameter "UP" innerhalb des Moduls "MD-2"
      //     MD-2_P-42     Parameter "P" innerhalb des Moduls "MD-2"
      //
      //     P-2           Globaler Parameter "P"
      //
      // Mutmassliche Syntax der ParamenterReferenz-Namen:
      //     P-6_R-5       Globale Parameter Referenz
      //     MD-2_P-3_R-3  Module Parameter Referenz
      //

      res = this.getValueIntern(id1, "");
      if (res != undefined) {
        logger.log(`getValueIntern global ${id1}: "${res}"`);
      }
    }

    return res;
  }

  private getValueIntern(id: string, instanceId?: string, returnUnreferencedParam?: boolean): any {
    // id = this.filterRelation(id);

    // returnUnreferencedParam = true;

    if (id) {
      if (instanceId && instanceId != "") {
        const resModul: any = this.modules[instanceId];

        if (resModul) {
          const p = resModul?.params[id];
          if (!returnUnreferencedParam && p && !p.referenced) {
            // console.log("return unreferenced: ", id, instanceId, p);
            return undefined;
          }
          const res: any = p?.value;
          if (res == undefined) {
            if (resModul == undefined) {
              logger.error(`getValue: module not found ${instanceId}`);
            } else {
              // for (let i in resModul.params) {
              //   if (i.startsWith(id)) {
              //     const param = resModul.params[i];
              //     if (param) {
              //       return param.value;
              //     }
              //   }
              // }

              const valueGlobal = this.getValue(id, undefined, returnUnreferencedParam);

              if (valueGlobal) {
                // logger.log(
                //   `getValue: global value found id=${id} instance=${instanceId}`,
                // );
                return valueGlobal;
              }

              logger.error(`getValue: id in module not found id=${id} instance=${instanceId}`);
            }
          }
          return res;
        } else {
          logger.error(`getValue: module not found id=${id} instance=${instanceId}`);
          return undefined;
        }
      } else {
        const p = this.params[id];
        if (!returnUnreferencedParam && p && !p.referenced) {
          // console.log("return unreferenced: ", id, instanceId, p);
          return undefined;
        }
        const res: any = p?.value;
        return res;
      }
    } else {
      logger.error("empty id");
    }
  }

  getLevel(id: string, instanceId?: string): ValueLevels | undefined {
    let p: any = undefined;

    if (id) {
      if (instanceId && instanceId != "") {
        const resModul: any = this.modules[instanceId];

        if (resModul) {
          p = resModul?.params[id];
        }
      } else {
        p = this.params[id];
      }
    }

    return p?.valueLevel;
  }

  getIdByRelation(rel: string, instanceId?: string): string | undefined {
    let params: Record<string, KnxInstanceParam> = this.params;
    if (instanceId && instanceId != "") {
      const module = this.modules[instanceId];

      if (module == undefined) {
        logger.error(`getIdByRelation: module not found ${instanceId}`);
        return undefined;
      }
      params = module.params;
    }

    var itemKey: string | undefined;
    for (const key in params) {
      if (key.endsWith("R-" + rel)) {
        itemKey = key;
        break;
      }
    }

    return itemKey;
  }

  setExtendedData(extData: any, id: string, instanceId?: string, overwrite?: boolean) {
    if (instanceId && !id.startsWith("MD-")) {
      instanceId = "";
    }

    if (instanceId && instanceId != "") {
      var moduleData = this.modules[instanceId];
      // create module if not yet existing
      if (moduleData == undefined) {
        moduleData = { params: {}, extData: {} };
        this.modules[instanceId] = moduleData;
      }

      if (!moduleData.extData) {
        moduleData.extData = [];
      }

      if (extData == undefined) {
        // delete extdata{
        moduleData.extData[id] = undefined;
      } else {
        if (overwrite) {
          moduleData.extData[id] = { ...extData };
        } else {
          moduleData.extData[id] = { ...moduleData.extData[id], ...extData };
        }
      }
    } else {
      if (extData == undefined) {
        // delete extdata{
        this.extData[id] = undefined;
      } else {
        if (overwrite) {
          this.extData[id] = { ...extData };
        } else {
          this.extData[id] = { ...this.extData[id], ...extData };
        }
      }
    }
  }

  setReferenced(id: string, instanceId?: string, uiReferenced: boolean = false) {
    // console.log("setReferenced ", id, instanceId);

    if (instanceId && !id.startsWith("MD-")) {
      instanceId = "";
    }

    let modified = false;
    let valueLevel: ValueLevels | undefined = ValueLevels.default;

    if (instanceId && instanceId != "") {
      if (!this.modules[instanceId]?.params[id]) {
        this.modules[instanceId].params[id] = {};
      }
      if (!this.modules[instanceId].params[id].referenced) {
        modified = true;
        valueLevel = this.modules[instanceId].params[id].valueLevel;
        this.modules[instanceId].params[id].referenced = true;
      }

      this.modules[instanceId].params[id].uiReferenced ||= uiReferenced;
    } else {
      if (!this.params[id]) {
        this.params[id] = {};
      }
      if (!this.params[id].referenced) {
        modified = true;
        this.params[id].referenced = true;
        valueLevel = this.params[id].valueLevel;
      }
      this.params[id].uiReferenced ||= uiReferenced;
    }

    valueLevel ??= ValueLevels.default;

    ////////////////////////////////////////////////////////////////////////////////////////////////////
    // MWE 24.9.2026: diese Optimierung ist nicht valide:
    //
    // zb THEBEN LUXA 103 S360-28 KNX UP WH KNX-Präsenzmelder | Erfassungsbereich 28 m
    // funktioniert dann nict mehr
    //
    // if (valueLevel <= ValueLevels.device && modified && this.reEvalIds?.has((instanceId || "") + id)) {
    //
    ///////////////////////////////////////////////////////////////////////////////////////////////////////

    if (modified && this.reEvalIds?.has((instanceId || "") + id)) {
      //
      // ReEvaluation needed, because a choose parameter is changed
      // and the maybe more parameters, other signales etc.
      if (this.parent) {
        if ((this.parent as BaseModel).isDevice) {
          const debugS = `setReferenced  ${instanceId}/${id}`;
          logger.log(`reeval: ${debugS}`);

          (this.parent as Device).setReEvalNeeded(debugS);
        }
      }
      // this.increaseReEvalCount(id, instanceId || "");
    }
  }

  getReferenced(id: string, instanceId?: string): boolean {
    if (instanceId && instanceId != "") {
      return this.modules[instanceId]?.params[id]?.referenced || false;
    } else {
      return this.params[id]?.referenced || false;
    }
  }

  getUiReferenced(id: string, instanceId?: string): boolean {
    if (instanceId && instanceId != "") {
      return this.modules[instanceId]?.params[id]?.uiReferenced || false;
    } else {
      return this.params[id]?.uiReferenced || false;
    }
  }

  fromWildCardToFullId(id: string, instanceId?: string): string | undefined {
    if (id?.endsWith("*")) {
      const id1 = this.findFullId(id, instanceId);
      if (id1) {
        return id1;
      } else {
        logger.error(`setValue: wildcard-id not found ${id} ${instanceId}`);
        return undefined;
      }
    } else {
      return id;
    }
  }

  getExtendedData(id: string, instanceId?: string): any | undefined {
    const id1 = this.fromWildCardToFullId(id, instanceId);
    if (!id1) {
      return undefined;
    }

    if (instanceId && instanceId != "") {
      const ed = this.modules[instanceId]?.extData;
      if (ed) {
        return ed[id1];
      } else {
        return undefined;
      }
    } else {
      return this.extData[id1];
    }
  }

  deleteValue(id: string, instanceId?: string) {
    if (instanceId && instanceId != "") {
      //
      // set data for module
      //
      var moduleData: any = this.modules[instanceId];
      if (moduleData) {
        delete moduleData.params[id];
      }
    } else {
      delete this.params[id];
    }
  }
  findFullId(id: string, instanceId?: string): string | undefined {
    id = id.substring(0, id.length - 1);
    var params: any;

    if (instanceId) {
      params = this.modules[instanceId].params;
    } else {
      params = this.params;
    }

    for (let key in params) {
      if (key.startsWith(id)) {
        if (params[key].referenced) {
          return key;
        }
      }
    }

    return undefined;
  }

  //
  // return true if value is a reEval Parameter is modified
  // returns false if value stays the same as before
  //

  setValue(
    valueLevel: ValueLevels,
    data: any,
    id: string,
    instanceId: string,
    moduleId: string,
    supressChangeEvent?: boolean,
    setByScript?: boolean,
  ): boolean {
    const id1 = this.fromWildCardToFullId(id, instanceId);
    if (!id1) {
      return false;
    }

    if (data == undefined) {
      data = this.getValue(id1, instanceId, true);
    } else if (typeof data !== "string") {
      try {
        const f = parseFloat(data);
        if (!Number.isNaN(f)) {
          data = f.toString();
        }
      } catch (ex) {}
    }

    // if (!supressChangeEvent) {
    //   // set flag that reevaluation is necessary, as we have assigned a new value to a paramref
    //   // that was already used with its old value in a choose before
    //   let lastValue = this.getValue(id1, instanceId, true);
    //   if (this.reEvalIds.has(instanceId + id1) && lastValue != data) {
    //     if ((this.getLevel(id1, instanceId) ?? 0) <= valueLevel) {
    //       this.increaseReEvalCount(id1, instanceId, data, lastValue, "A");
    //     } else if (!this.getReferenced(id1, instanceId)) {
    //       // setting the param should result in this value being referenced (i.e. relevant for evaluating chooses), no matter
    //       // if the valuelevel was lower. Thus, if settting this value would result in a flip of this boolean, we will also need to reevaluate.
    //       this.increaseReEvalCount(id1, instanceId, data, lastValue, "B");
    //     }
    //   }
    // }

    /////////////////////////////////////////////////////////////////////////
    //
    // handle unions
    //

    // const knxProductData = (this.parent as Device)?.template?.knxProductData;
    // const union = knxProductData?.memory.get(id);
    // if (union) {
    //   for (let i = 0; i < union.parameterRefIds.length; i++) {
    //     const xid = union.parameterRefIds[i];
    //     if (xid != id) {
    //       this.setValueIntern(valueLevel, data, xid, instanceId, moduleId, supressChangeEvent, setByScript);
    //     }
    //   }
    // }
    ///////////////////////////////////////////////////////////////////////////

    if (valueLevel >= ValueLevels.force) {
      //
      // delete current value
      //
      this.setValueIntern(ValueLevels.delete, undefined, id1, instanceId, moduleId, supressChangeEvent, setByScript);
      //
      // convert valueLevel to "normal" value
      //
      valueLevel -= ValueLevels.force;
    }

    // if we are deleting the parameter, the data is irrelevant, as we will be recomputing the defaults.
    // however, deleting and immedaitely setting the value with the same data would result in wrong behaviour
    // (i.e. setting channel type in one button). Thus, we set the data to undefined here.
    if (valueLevel == ValueLevels.delete) {
      data = undefined;
    }

    //
    // Texte mit Längenbeschränkung kürzen
    //
    const extData = this.getExtendedData(id, instanceId);
    if (extData?.maxLength) {
      if (typeof data == "string" && data.length > extData.maxLength) {
        data = data.substring(0, extData.maxLength);
      }
    }

    return this.setValueIntern(valueLevel, data, id1, instanceId, moduleId, supressChangeEvent, setByScript);
  }

  private setValueIntern(
    valueLevel: ValueLevels,
    data: any,
    id: string,
    instanceId: string,
    moduleId: string,
    supressChangeEvent?: boolean,
    setByScript?: boolean,
  ): boolean {
    // id = this.filterRelation(id);

    const valueLevelOrg = valueLevel;

    //
    //  MWE 24.6.2024
    //
    // Diesen Code erstmal raus genommen,
    // ich bin mir allerding nicht sicher,
    // ob der nicht einen leider nicht dokumentierten Trick für Geräte mit Berechnunge darstellte
    // ====> noch prüfen !!!!!!

    // if (valueLevel == ValueLevels.calculated) {
    //   valueLevel = ValueLevels.interactive;
    // }

    var lastValue: any | undefined = undefined;
    var changedParameter: any | undefined = undefined;
    let currentValueLevel = ValueLevels.default;
    let undoChangeStarted = false;
    const beforeParameterChange = () => {
      if (undoChangeStarted) return;
      this.beforeChange("parameters");
      undoChangeStarted = true;
    };
    const finishParameterChange = () => {
      if (undoChangeStarted) this.changedForUndo("parameters");
    };

    if (instanceId && !id.startsWith("MD-")) {
      logger.log(`FIXED: set global var ??? id=${id} instanceId=${instanceId}`);
      instanceId = "";
    }

    if (instanceId && instanceId != "") {
      //
      // set data for module
      //
      var moduleData: any = this.modules[instanceId];

      // create module if not yet existing
      if (moduleData == undefined) {
        beforeParameterChange();
        moduleData = { params: {}, extData: {} };
        this.modules[instanceId] = moduleData;
      }

      if (moduleData.params[id]) {
        lastValue = moduleData.params[id].value;
        currentValueLevel = moduleData.params[id].valueLevel ?? ValueLevels.default;
      } else {
   
        beforeParameterChange();
        moduleData.params[id] = {};
      }

      if (valueLevel == ValueLevels.keepLevel) {
        valueLevel = currentValueLevel;
      }

      /////////////////////////   mwe 3.6.2026 moduleData.params[id].referenced = true;

      if (valueLevel == ValueLevels.delete) {
        beforeParameterChange();
        valueLevel = ValueLevels.default;
        moduleData.params[id].valueLevel = currentValueLevel = ValueLevels.default;
      }

      if (currentValueLevel <= valueLevel) {
        // this.setMemoryValue(id, instanceId, data, valueLevel)

        beforeParameterChange();
        moduleData.params[id].value = data;
        moduleData.params[id].valueLevel = valueLevel;

        moduleData.params[id].setByScript = setByScript;

        changedParameter = moduleData.params[id];
      } else {
        // logger.log(`skip setting of value`);
      }
    } else {
      //
      // set data for "main-module"
      //

      if (this.params[id]) {
        lastValue = this.params[id].value;
        currentValueLevel = this.params[id].valueLevel ?? ValueLevels.default;
      } else {
        beforeParameterChange();
        this.params[id] = {};
      }

      if (valueLevel == ValueLevels.keepLevel) {
        valueLevel = currentValueLevel;
      }

      /////////////////////////   mwe 3.6.2026 this.params[id].referenced = true;

      if (valueLevel == ValueLevels.delete) {
        beforeParameterChange();
        valueLevel = ValueLevels.default;
        this.params[id].valueLevel = currentValueLevel = ValueLevels.default;
      }

      if (lastValue != data) {
        if (currentValueLevel <= valueLevel) {
          // this.setMemoryValue(id, instanceId, data, valueLevel)

          beforeParameterChange();
          this.params[id].value = data;
          this.params[id].valueLevel = valueLevel;
          this.params[id].setByScript = setByScript;

          //
          // Stand 15.1.2025 unterstützen wir keine OnChange Events für einzelne Parameter im Customizing mehr
          // bei Bedarf müsste man das hier, und dann wohl nur für interactive Werte
          // wieder reaktivieren
          //
          // if (!supressChangeEvent) {
          //   const knxProductData = (this.parent as Device)?.template?.knxProductData;
          //   const rules = knxProductData?.currentRules;

          //   rules && rules.executeOnChange(this.parent as Device, this, id, instanceId, moduleId, data, lastValue);
          // }

          changedParameter = this.params[id];
        } else {
          // logger.log(`skip setting of value`);
        }
      }
    }

    //    if (valueLevel != ValueLevels.default && valueLevelOrg != ValueLevels.calculated && data != lastValue) {
    if (valueLevel != ValueLevels.default && data != lastValue) {
      const refArray = this.paramsToCalculationReferences.get(id);
      if (refArray) {
        refArray.forEach((ref) => {
          let doEval = true;

          if (valueLevelOrg == ValueLevels.calculatedLeftToRight) {
            //
            // der Wert ist das Ergebnis einer LeftToRightCalculalation
            //
            // In diesem Falle soll KEINE RightToLeft-Calculation für diesen Parameter gestartet werden!
            //
            if (ref.side == "R") {
              doEval = false;
            }
          }

          if (valueLevelOrg == ValueLevels.calculatedRightToLeft) {
            //
            // Spiegelbildlich zu oben ...
            //
            if (ref.side == "L") {
              doEval = false;
            }
          }

          if (doEval) {
            const rightToLeft = ref.side == "R";
            const knxProductData = (this.parent as Device)?.template?.knxProductData;
            if (knxProductData) {
              ref.knxParameterCalculation.eval(rightToLeft, knxProductData, this, instanceId, id);
            }
          }
        });
      }
    }

    if (data != lastValue) {
      if (valueLevel >= ValueLevels.device && currentValueLevel <= valueLevel) {
        let res = false;
        if (this.reEvalIds?.has((instanceId || "") + id)) {
          const debugS = `setValue: reEval param changed  ${instanceId}/${id}  ${lastValue}-> ${data} (${valueLevel})`;
          logger.log(debugS);
          res = true;

          // this.increaseReEvalCount(id, instanceId);

          //
          // ReEvaluation needed, because a choose parameter is changed
          // and the maybe more parameters, other signales etc.
          if (this.parent) {
            if ((this.parent as BaseModel).isDevice) {
              (this.parent as Device).setReEvalNeeded(debugS);
            }
          }
        }

        const target = this.assigns.get(id);
        if (target?.toId) {
          this.setValueIntern(valueLevel, data, target.toId, target.instanceId, moduleId);
        }

        EP(`raiseOnChange ${id}`).run(() => {
          if (!supressChangeEvent) {
            this.raiseOnChange(changedParameter);
          }
        });
        //      this.parent?.raiseOnChange("knxInstanceData");
        finishParameterChange();
        return res;
      }
    }
    finishParameterChange();
    return false;
  }

  increaseReEvalCount(id: string, instanceId: string) {
    if (!this.reEvalIds?.has((instanceId || "") + id)) {
      return;
    }

    if (isDevelopmentEnvironment()) {
      // if isSet == true, keep it
      const currentValueExt = this.getExtendedData(id, instanceId);
      const reeval = currentValueExt?.reeval;

      this.setExtendedData(
        {
          reeval: {
            count: (reeval?.count || 0) + 1,
          },
        },
        id,
        instanceId,
      );
    }
  }

  resetReEvalCount(id: string, instanceId: string) {
    if (isDevelopmentEnvironment()) {
      // if isSet == true, keep it
      const currentValueExt = this.getExtendedData(id, instanceId);
      const reeval = currentValueExt?.reeval;

      this.setExtendedData(
        {
          reeval: {
            count: (reeval?.count || 0) + 1,
          },
        },
        id,
        instanceId,
        true,
      );
    }
  }
  // setMemoryValue(id: string, instanceId: string | undefined, value: any, valueLevel: ValueLevels) {

  //   const ext = this.getExtendedData(id, instanceId);
  //   const mem = ext?.mem;

  //   if (mem) {
  //     const mem_index = (instanceId || "") + "-" + mem.Offset + "-" + mem.BitOffset
  //     this.memory.set(mem_index, { value: value, level: valueLevel });
  //     console.log(`setMemoryValue instanceId=${instanceId} id=${id} value=${value} `)
  //   }
  // }

  // getMemeoryValue(id: string, instanceId: string | undefined, mem: any): any | undefined {
  //   if (mem) {
  //     const mem_index = (instanceId || "") + "-" + mem.Offset + "-" + mem.BitOffset
  //     const v = this.memory.get(mem_index);
  //     if (v) {
  //       return v.value
  //     }
  //   }

  //   return undefined
  // }

  copy(): KnxInstanceData {
    const res = new KnxInstanceData({}, this.parent);
    res.fromJSON(this.toJSON());
    return res;
  }

  raiseOnChange(changedProperty: string | any | undefined = undefined, eventSource?: string, uiOnly?: boolean) {
    super.raiseOnChange(changedProperty, eventSource, uiOnly);
    // this.parent?.raiseOnChange("knxInstanceData", eventSource);
  }

  ///////////////////////////////////////////////////////////////////////////////////
  attachParameterCalculation(instanceId: string, parameterCalculation: KnxParameterCalculation) {
    if (this.parameterCalculations.has(parameterCalculation.id)) {
      return;
    }

    this.parameterCalculations.set(parameterCalculation.id, parameterCalculation);

    // attach Calculation to params
    parameterCalculation.lParams.forEach((item, key) => {
      let refMap = this.paramsToCalculationReferences.get(key);
      if (!refMap) {
        refMap = new Map<string, KnxParameterCalculationReference>();
        this.paramsToCalculationReferences.set(key, refMap);
      }

      refMap.set(parameterCalculation.id, new KnxParameterCalculationReference("L", parameterCalculation));
    });

    parameterCalculation.rParams.forEach((item, key) => {
      let refMap = this.paramsToCalculationReferences.get(key);
      if (!refMap) {
        refMap = new Map<string, KnxParameterCalculationReference>();
        this.paramsToCalculationReferences.set(key, refMap);
      }

      refMap.set(parameterCalculation.id, new KnxParameterCalculationReference("R", parameterCalculation));
    });
  }

  allModuleIds(): Array<string> {
    const res = new Array<string>("");

    for (const i in this.modules) {
      res.push(i);
    }

    return res;
  }

  allPropertyIds(instanceId?: string): Array<string> {
    const res = new Array<string>();

    if (instanceId) {
      const m = this.modules[instanceId];
      if (m?.params) {
        for (const i in m.params) {
          res.push(i);
        }
      }
    } else {
      for (const i in this.params) {
        res.push(i);
      }
    }

    return res;
  }

  executeOnecDone = false;
  executeAllCalculationsOnce(moduleId: string, instanceId: string, ifIdsAreInputParameters: Array<string> | undefined) {
    const knxProductData = (this.parent as Device)?.template?.knxProductData;

    //    if (!this.executeOnecDone && knxProductData) {
    if (knxProductData) {
      this.executeOnecDone = true;
      this.parameterCalculations.forEach((parameterCalculation) => {
        if (parameterCalculation.moduleId === moduleId) {
          let useLeftToRight = false;
          let useRightToLeft = false;

          if (ifIdsAreInputParameters) {
            for (let [key, item] of parameterCalculation.lParams) {
              if (ifIdsAreInputParameters.includes(key)) {
                useLeftToRight = true;
              }
            }
            for (let [key, item] of parameterCalculation.rParams) {
              if (ifIdsAreInputParameters.includes(key)) {
                useRightToLeft = true;
              }
            }

            if (useLeftToRight && useRightToLeft) {
              logger.log(
                `executeAllCalculationsOnce: both leftToRight and rightToLeft for ${parameterCalculation.id} module ${moduleId} instance ${instanceId} `,
              );
            }
            if (!useLeftToRight && !useRightToLeft) {
              logger.log(
                `executeAllCalculationsOnce: NO leftToRight and rightToLeft for ${parameterCalculation.id} module ${moduleId} instance ${instanceId} `,
              );
            }
          } else {
            useLeftToRight = true;
            useRightToLeft = true;
          }

          if (useLeftToRight) {
            parameterCalculation.eval(false, knxProductData, this, instanceId, parameterCalculation.id);
          }
          if (useRightToLeft) {
            parameterCalculation.eval(true, knxProductData, this, instanceId, parameterCalculation.id);
          }
          // parameterCalculation.eval(true, knxProductData, this, instanceId, parameterCalculation.id);
        }
      });
    }
  }

  clearParameterCalculations() {
    this.parameterCalculations = new Map<string, KnxParameterCalculation>();
  }

  loadDefaultsFromConfig() {
    const knxProductData = (this.parent as Device)?.template?.knxProductData;
    if (knxProductData) {
      knxProductData.config.loadParams(this);
    }
    // setting defaults should not set deeply nested params as referenced as this leads to bad w
    //  Choose/When evaluations. (e.g. GVS CHTFB-3.0/6.x.yz disabling Function page Icon 2 does not disable Function page 4 as expected)

    //// mew 10.6.26 this.clearReferenced();
  }

  getAllExtendedDatas(): Array<{ id: string; instanceId: string; extData: any }> {
    const res = new Array<{ id: string; instanceId: string; extData: any }>();

    for (const instanceId in this.modules) {
      var moduleData = this.modules[instanceId];
      // create module if not yet existing
      const ext = moduleData?.extData || {};
      for (const id in ext) {
        if (ext[id]) {
          res.push({
            id,
            instanceId,
            extData: ext[id],
          });
        }
      }
    }

    for (const id in this.extData) {
      res.push({
        id,
        instanceId: "",
        extData: this.extData[id],
      });
    }

    return res;
  }
}
